import { useEffect, useState } from 'react';
import * as publicPetitionService from '../../../services/publicPetitionCaseRequests';
import type { LookupItem } from '../../../mocks/criminalMasterData';

export type PublicPetitionLookupScope = 'step1' | 'step2' | 'step3' | 'step4' | 'step5' | 'review';

type LookupBundle = {
  provinces: LookupItem[];
  districts: LookupItem[];
  respondentTypes: LookupItem[];
  urgencyLevels: LookupItem[];
  deliveryMethods: LookupItem[];
};

const EMPTY: LookupBundle = {
  provinces: [],
  districts: [],
  respondentTypes: [],
  urgencyLevels: [],
  deliveryMethods: [],
};

let sharedLookups: LookupBundle = { ...EMPTY };

export function usePublicPetitionLookups(scope: PublicPetitionLookupScope, provinceId?: string) {
  const [data, setData] = useState<LookupBundle>(sharedLookups);
  const [loading, setLoading] = useState(scope !== 'step5');

  useEffect(() => {
    if (scope === 'step5') {
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(true);

    const load = async (): Promise<Partial<LookupBundle>> => {
      if (scope === 'step1') {
        const prov = await publicPetitionService.getProvinces();
        return { provinces: prov.success ? prov.data : [] };
      }
      if (scope === 'step2') {
        const types = await publicPetitionService.getRespondentTypes();
        return { respondentTypes: types.success ? types.data : [] };
      }
      if (scope === 'step3') {
        const urg = await publicPetitionService.getUrgencyLevels();
        return { urgencyLevels: urg.success ? urg.data : [] };
      }
      if (scope === 'step4') {
        const del = await publicPetitionService.getDeliveryMethods();
        return { deliveryMethods: del.success ? del.data : [] };
      }
      const [prov, types, urg, del] = await Promise.all([
        publicPetitionService.getProvinces(),
        publicPetitionService.getRespondentTypes(),
        publicPetitionService.getUrgencyLevels(),
        publicPetitionService.getDeliveryMethods(),
      ]);
      return {
        provinces: prov.success ? prov.data : [],
        respondentTypes: types.success ? types.data : [],
        urgencyLevels: urg.success ? urg.data : [],
        deliveryMethods: del.success ? del.data : [],
      };
    };

    void load().then((partial) => {
      if (!mounted) return;
      sharedLookups = { ...sharedLookups, ...partial };
      setData({ ...sharedLookups });
      setLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, [scope]);

  useEffect(() => {
    if (scope !== 'step1' || !provinceId) {
      if (scope === 'step1' && !provinceId) {
        sharedLookups = { ...sharedLookups, districts: [] };
        setData({ ...sharedLookups });
      }
      return;
    }

    let mounted = true;
    void publicPetitionService.getDistricts(provinceId).then((r) => {
      if (!mounted) return;
      const districts = r.success ? r.data : [];
      sharedLookups = { ...sharedLookups, districts };
      setData({ ...sharedLookups });
    });

    return () => {
      mounted = false;
    };
  }, [scope, provinceId]);

  return {
    loading,
    provinces: data.provinces,
    districts: data.districts,
    respondentTypes: data.respondentTypes,
    urgencyLevels: data.urgencyLevels,
    deliveryMethods: data.deliveryMethods,
  };
}
